import { useState } from 'react'
import { usePresupuestos } from './usePresupuestos'
import { ModalMaestroInsumos } from '../insumos/ModalMaestroInsumos'
import { ModalSelectorApu } from './ModalSelectorApu'
import { ModalVersionesPresupuesto } from './ModalVersionesPresupuesto'
import { exportarPresupuestoExcel } from './exportarPresupuestoExcel'
import { imprimirPresupuestoPdf, generarEnlaceWhatsAppPresupuesto } from './exportarPresupuestoPdf'
import type { Apu } from '../apus/tipos'
import { ModalConfirmacion } from '@/shared/ui/Modal/ModalConfirmacion'
import styles from './PresupuestadorPage.module.css'

export function PresupuestadorPage() {
  const {
    presupuestos,
    presupuestoActivo,
    presupuestoSeleccionadoId,
    setPresupuestoSeleccionadoId,
    crearPresupuesto,
    actualizarPresupuesto,
    eliminarPresupuesto,
    agregarCapitulo,
    eliminarCapitulo,
    renombrarCapitulo,
    agregarItemDesdeApu,
    actualizarCantidadItem,
    eliminarItemCapitulo,
    actualizarAiuPresupuesto,
    guardarVersionPresupuesto,
    restaurarVersionPresupuesto,
    ganarPresupuestoYConvertirEnObra,
  } = usePresupuestos()

  // Modales
  const [modalInsumosAbierto, setModalInsumosAbierto] = useState(false)
  const [modalVersionesAbierto, setModalVersionesAbierto] = useState(false)
  const [capituloSeleccionadoApu, setCapituloSeleccionadoApu] = useState<{ id: string; nombre: string } | null>(null)

  // Mensaje de éxito / conversión
  const [mensajeExito, setMensajeExito] = useState<string | null>(null)
  const [convirtiendo, setConvirtiendo] = useState(false)
  const [modalConfirmacion, setModalConfirmacion] = useState<{
    abierto: boolean
    titulo: string
    mensaje: string
    submensaje?: string
    textoConfirmar?: string
    tipo?: 'peligro' | 'primario' | 'advertencia'
    alConfirmar: () => void
  }>({
    abierto: false,
    titulo: '',
    mensaje: '',
    alConfirmar: () => {},
  })

  function pedirConfirmacion(config: {
    titulo: string
    mensaje: string
    submensaje?: string
    textoConfirmar?: string
    tipo?: 'peligro' | 'primario' | 'advertencia'
    alConfirmar: () => void
  }) {
    setModalConfirmacion({
      ...config,
      abierto: true,
    })
  }

  const formatearCOP = (val: number) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0)

  if (!presupuestoActivo) {
    return (
      <div className={styles.sinPresupuesto}>
        <h3>No hay presupuestos registrados</h3>
        <p>Cree un presupuesto para cotizar actividades basadas en el banco de APUs.</p>
        <button
          type="button"
          className={styles.btnPrimario}
          onClick={() => crearPresupuesto({ nombre: 'Nuevo Presupuesto General' })}
        >
          + Crear Primer Presupuesto
        </button>
      </div>
    )
  }

  const handleCrearNuevoPresupuesto = () => {
    const nombre = prompt('Nombre del nuevo presupuesto:')
    if (!nombre) return
    const cliente = prompt('Nombre del cliente / contratante:', 'MEGA DISTRIBUCIONES SAS')
    crearPresupuesto({
      nombre,
      cliente: cliente || 'GENERAL',
    })
  }

  const handleConvertirEnObra = () => {
    if (presupuestoActivo.estado === 'aprobado' && presupuestoActivo.proyecto_id) {
      pedirConfirmacion({
        titulo: 'Presupuesto Ya Convertido',
        mensaje: `Este presupuesto (${presupuestoActivo.codigo}) ya fue convertido previamente en obra.`,
        submensaje: `ID de Obra: ${presupuestoActivo.proyecto_id}. Puede consultarla en la pestaña Plan & Gantt.`,
        textoConfirmar: 'Entendido',
        tipo: 'primario',
        alConfirmar: () => {},
      })
      return
    }

    pedirConfirmacion({
      titulo: 'Convertir a Obra Activa',
      mensaje: `¿Desea aprobar y convertir el presupuesto ${presupuestoActivo.codigo} en Obra Activa?`,
      submensaje: `Se generarán ${presupuestoActivo.capitulos.length} etapas en el Plan de Proyectos y todas las actividades pasarán a ser módulos con cantidades contratadas fijas.`,
      textoConfirmar: 'Convertir a Obra',
      tipo: 'primario',
      alConfirmar: async () => {
        try {
          setConvirtiendo(true)
          const nuevoProyectoId = await ganarPresupuestoYConvertirEnObra(presupuestoActivo.id)
          if (nuevoProyectoId) {
            setMensajeExito(
              '¡Presupuesto aprobado y convertido exitosamente en Obra! Puede verlo en el Tablero de Proyectos o Plan & Gantt.'
            )
          }
        } catch (err) {
          console.error('Error al convertir en obra:', err)
          pedirConfirmacion({
            titulo: 'Error de Conversión',
            mensaje: 'Ocurrió un error al convertir el presupuesto en obra.',
            tipo: 'peligro',
            alConfirmar: () => {},
          })
        } finally {
          setConvirtiendo(false)
        }
      },
    })
  }

  const aiu = presupuestoActivo.aiu || {
    porcentaje_admin: 10,
    porcentaje_imprevistos: 0,
    porcentaje_utilidad: 3,
    porcentaje_iva_utilidad: 19,
  }

  const costoDirecto = presupuestoActivo.costo_directo_total
  const valAdmin = costoDirecto * ((aiu.porcentaje_admin || 0) / 100)
  const valImprev = costoDirecto * ((aiu.porcentaje_imprevistos || 0) / 100)
  const valUtil = costoDirecto * ((aiu.porcentaje_utilidad || 0) / 100)
  const valIvaUtil = valUtil * ((aiu.porcentaje_iva_utilidad || 19) / 100)

  return (
    <div className={styles.contenedor}>
      {/* Barra Superior de Control y Exportación */}
      <div className={styles.barraSuperior}>
        <div className={styles.selectorWrapper}>
          <label className={styles.labelSelect}>Presupuesto Activo:</label>
          <select
            value={presupuestoSeleccionadoId || ''}
            onChange={(e) => setPresupuestoSeleccionadoId(e.target.value)}
            className={styles.selectPresupuesto}
          >
            {presupuestos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} - {p.nombre} ({formatearCOP(p.total_presupuesto)})
              </option>
            ))}
          </select>
          <button
            type="button"
            className={styles.btnNuevoPres}
            onClick={handleCrearNuevoPresupuesto}
            title="Crear un nuevo presupuesto"
          >
            + Nuevo
          </button>
          {presupuestos.length > 1 && (
            <button
              type="button"
              className={styles.btnNuevoPres}
              style={{ color: '#dc2626' }}
              onClick={() => {
                pedirConfirmacion({
                  titulo: 'Eliminar Presupuesto',
                  mensaje: `¿Está seguro de eliminar el presupuesto ${presupuestoActivo.codigo}?`,
                  submensaje: 'Esta acción no se puede deshacer y retirará todos sus capítulos.',
                  textoConfirmar: 'Eliminar',
                  tipo: 'peligro',
                  alConfirmar: () => eliminarPresupuesto(presupuestoActivo.id),
                })
              }}
              title="Eliminar presupuesto actual"
            >
              Eliminar
            </button>
          )}
        </div>

        <div className={styles.accionesHeader}>
          <button
            type="button"
            className={styles.btnMaestroInsumos}
            onClick={() => setModalInsumosAbierto(true)}
            title="Abrir Maestro de Insumos y Mano de Obra"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
            <span>Maestro Insumos</span>
          </button>

          <button
            type="button"
            className={styles.btnVersiones}
            onClick={() => setModalVersionesAbierto(true)}
            title="Historial de versiones y comparador"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>Versiones ({presupuestoActivo.versiones?.length || 0})</span>
          </button>

          <button
            type="button"
            className={styles.btnExcel}
            onClick={() => exportarPresupuestoExcel(presupuestoActivo)}
            title="Exportar a Microsoft Excel con fórmulas"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="16" y2="17" />
            </svg>
            <span>Excel</span>
          </button>

          <button
            type="button"
            className={styles.btnPdf}
            onClick={() => imprimirPresupuestoPdf(presupuestoActivo)}
            title="Generar PDF con membrete corporativo"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            <span>Imprimir PDF</span>
          </button>

          <a
            href={generarEnlaceWhatsAppPresupuesto(presupuestoActivo)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.btnWhatsApp}
            title="Enviar cotización por WhatsApp al cliente"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
            <span>WhatsApp</span>
          </a>

          {/* Botón Ganar Presupuesto y Convertir en Obra (Tarea 3.8) */}
          <button
            type="button"
            className={`${styles.btnConvertirObra} ${presupuestoActivo.estado === 'aprobado' ? styles.btnYaAprobado : ''}`}
            onClick={handleConvertirEnObra}
            disabled={convirtiendo}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>
              {presupuestoActivo.estado === 'aprobado'
                ? 'Obra Aprobada Activa'
                : convirtiendo
                ? 'Convirtiendo...'
                : 'Convertir a Obra Activa'}
            </span>
          </button>
        </div>
      </div>

      {/* Banner de éxito */}
      {mensajeExito && (
        <div className={styles.bannerExito}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span>{mensajeExito}</span>
          <button type="button" onClick={() => setMensajeExito(null)} className={styles.cerrarBanner}>
            ✕
          </button>
        </div>
      )}

      {/* Ficha editable del presupuesto */}
      <div className={styles.fichaPresupuesto}>
        <div className={styles.fichaGrid}>
          <div>
            <div className={styles.codigoBadge}>{presupuestoActivo.codigo}</div>
            <input
              type="text"
              value={presupuestoActivo.nombre}
              onChange={(e) => actualizarPresupuesto(presupuestoActivo.id, { nombre: e.target.value })}
              className={styles.inputTituloPres}
              placeholder="Nombre del proyecto / cotización..."
            />
          </div>

          <div className={styles.camposFicha}>
            <div>
              <label>Cliente / Contratante:</label>
              <input
                type="text"
                value={presupuestoActivo.cliente || ''}
                onChange={(e) => actualizarPresupuesto(presupuestoActivo.id, { cliente: e.target.value })}
                className={styles.inputSimple}
                placeholder="Nombre del cliente"
              />
            </div>
            <div>
              <label>Ubicación / Ciudad:</label>
              <input
                type="text"
                value={presupuestoActivo.ubicacion || ''}
                onChange={(e) => actualizarPresupuesto(presupuestoActivo.id, { ubicacion: e.target.value })}
                className={styles.inputSimple}
                placeholder="Bucaramanga, Santander"
              />
            </div>
            <div>
              <label>Estado Contractual:</label>
              <select
                value={presupuestoActivo.estado}
                onChange={(e) => actualizarPresupuesto(presupuestoActivo.id, { estado: e.target.value as any })}
                className={styles.selectEstado}
              >
                <option value="borrador">Borrador</option>
                <option value="presentado">Presentado al Cliente</option>
                <option value="aprobado">Aprobado / Ganado</option>
                <option value="rechazado">Rechazado</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Capítulos y Actividades */}
      <div className={styles.capitulosContenedor}>
        <div className={styles.capitulosHeader}>
          <h3 className={styles.seccionTitulo}>Estructura de Capítulos y Actividades</h3>
          <button
            type="button"
            className={styles.btnAgregarCapitulo}
            onClick={() => agregarCapitulo(presupuestoActivo.id)}
          >
            + Añadir Nuevo Capítulo
          </button>
        </div>

        {presupuestoActivo.capitulos.length === 0 ? (
          <div className={styles.sinCapitulos}>
            <p>Este presupuesto no tiene capítulos definidos.</p>
            <button
              type="button"
              className={styles.btnPrimario}
              onClick={() => agregarCapitulo(presupuestoActivo.id)}
            >
              + Crear Primer Capítulo
            </button>
          </div>
        ) : (
          presupuestoActivo.capitulos.map((cap, capIdx) => (
            <div key={cap.id} className={styles.capituloCard}>
              <div className={styles.capHeader}>
                <div className={styles.capTituloBox}>
                  <span className={styles.capNumero}>Capítulo {capIdx + 1}</span>
                  <input
                    type="text"
                    value={cap.nombre}
                    onChange={(e) => renombrarCapitulo(presupuestoActivo.id, cap.id, e.target.value)}
                    className={styles.inputNombreCapitulo}
                  />
                </div>
                <div className={styles.capHeaderRight}>
                  <div className={styles.subtotalCapituloText}>
                    Subtotal Capítulo: <strong>{formatearCOP(cap.subtotal_capitulo)}</strong>
                  </div>
                  <button
                    type="button"
                    className={styles.btnAñadirApu}
                    onClick={() => setCapituloSeleccionadoApu({ id: cap.id, nombre: cap.nombre })}
                  >
                    + Añadir Actividad APU
                  </button>
                  <button
                    type="button"
                    className={styles.btnEliminarCapitulo}
                    onClick={() => {
                      pedirConfirmacion({
                        titulo: 'Eliminar Capítulo',
                        mensaje: `¿Está seguro de eliminar "${cap.nombre}"?`,
                        submensaje: 'Se retirarán también todas las actividades contenidas en este capítulo.',
                        textoConfirmar: 'Eliminar Capítulo',
                        tipo: 'peligro',
                        alConfirmar: () => eliminarCapitulo(presupuestoActivo.id, cap.id),
                      })
                    }}
                    title="Eliminar capítulo"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Tabla de ítems del capítulo */}
              <div className={styles.tablaWrapper}>
                <table className={styles.tablaItems}>
                  <thead>
                    <tr>
                      <th style={{ width: '45px', textAlign: 'center' }}>Ítem</th>
                      <th style={{ width: '120px' }}>Cód. APU</th>
                      <th>Descripción de Actividad</th>
                      <th style={{ width: '60px', textAlign: 'center' }}>Und</th>
                      <th style={{ width: '110px', textAlign: 'right' }}>Cantidad</th>
                      <th style={{ width: '140px', textAlign: 'right' }}>V. Unitario</th>
                      <th style={{ width: '150px', textAlign: 'right' }}>Subtotal</th>
                      <th style={{ width: '50px', textAlign: 'center' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cap.items.length === 0 ? (
                      <tr>
                        <td colSpan={8} className={styles.filaVacia}>
                          No hay actividades en este capítulo. Haga clic en <strong>&ldquo;+ Añadir Actividad APU&rdquo;</strong> para seleccionar del banco de precios unitarios.
                        </td>
                      </tr>
                    ) : (
                      cap.items.map((item, itemIdx) => (
                        <tr key={item.id}>
                          <td style={{ textAlign: 'center', color: '#64748b', fontWeight: 600 }}>
                            {capIdx + 1}.{itemIdx + 1}
                          </td>
                          <td className={styles.codigoApuTd}>{item.codigo}</td>
                          <td className={styles.nombreApuTd}>{item.nombre}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700 }}>{item.unidad}</td>
                          <td style={{ textAlign: 'right' }}>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={item.cantidad}
                              onChange={(e) =>
                                actualizarCantidadItem(
                                  presupuestoActivo.id,
                                  cap.id,
                                  item.id,
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className={styles.inputCantidadItem}
                            />
                          </td>
                          <td style={{ textAlign: 'right', color: '#475569' }}>
                            {formatearCOP(item.precio_unitario)}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                            {formatearCOP(item.subtotal)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              className={styles.btnBorrarItem}
                              onClick={() => eliminarItemCapitulo(presupuestoActivo.id, cap.id, item.id)}
                              title="Eliminar actividad"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Barra de AIU Global y Totales (Tarea 3.5) */}
      <div className={styles.aiuBarra}>
        <div className={styles.aiuConfigurador}>
          <div className={styles.aiuTitulo}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            <span>Cálculo de AIU Contractual:</span>
          </div>
          <div className={styles.aiuInputsGrid}>
            <div className={styles.aiuInputItem}>
              <label>Admin (%):</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={aiu.porcentaje_admin}
                onChange={(e) =>
                  actualizarAiuPresupuesto(presupuestoActivo.id, {
                    ...aiu,
                    porcentaje_admin: parseFloat(e.target.value) || 0,
                  })
                }
                className={styles.inputAiu}
              />
              <span className={styles.aiuMonto}>{formatearCOP(valAdmin)}</span>
            </div>

            <div className={styles.aiuInputItem}>
              <label>Imprevistos (%):</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={aiu.porcentaje_imprevistos}
                onChange={(e) =>
                  actualizarAiuPresupuesto(presupuestoActivo.id, {
                    ...aiu,
                    porcentaje_imprevistos: parseFloat(e.target.value) || 0,
                  })
                }
                className={styles.inputAiu}
              />
              <span className={styles.aiuMonto}>{formatearCOP(valImprev)}</span>
            </div>

            <div className={styles.aiuInputItem}>
              <label>Utilidad (%):</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={aiu.porcentaje_utilidad}
                onChange={(e) =>
                  actualizarAiuPresupuesto(presupuestoActivo.id, {
                    ...aiu,
                    porcentaje_utilidad: parseFloat(e.target.value) || 0,
                  })
                }
                className={styles.inputAiu}
              />
              <span className={styles.aiuMonto}>{formatearCOP(valUtil)}</span>
            </div>

            <div className={styles.aiuInputItem}>
              <label>IVA s/ Util (%):</label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={aiu.porcentaje_iva_utilidad}
                onChange={(e) =>
                  actualizarAiuPresupuesto(presupuestoActivo.id, {
                    ...aiu,
                    porcentaje_iva_utilidad: parseFloat(e.target.value) || 0,
                  })
                }
                className={styles.inputAiu}
              />
              <span className={styles.aiuMonto}>{formatearCOP(valIvaUtil)}</span>
            </div>
          </div>
        </div>

        {/* Resumen Final de Cifras */}
        <div className={styles.resumenTotales}>
          <div className={styles.tileCostoDirecto}>
            <div className={styles.tileLabel}>Costo Directo Total</div>
            <div className={styles.tileValor}>{formatearCOP(costoDirecto)}</div>
          </div>
          <div className={styles.tileAiu}>
            <div className={styles.tileLabel}>Valor AIU Total</div>
            <div className={styles.tileValor}>{formatearCOP(presupuestoActivo.valor_aiu_total)}</div>
          </div>
          <div className={styles.tileGranTotal}>
            <div className={styles.tileLabelGran}>TOTAL PRESUPUESTO</div>
            <div className={styles.tileValorGran}>{formatearCOP(presupuestoActivo.total_presupuesto)}</div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmación Estilizado (CERO ventana nativa de navegador) */}
      <ModalConfirmacion
        abierto={modalConfirmacion.abierto}
        titulo={modalConfirmacion.titulo}
        mensaje={modalConfirmacion.mensaje}
        submensaje={modalConfirmacion.submensaje}
        textoConfirmar={modalConfirmacion.textoConfirmar}
        tipo={modalConfirmacion.tipo}
        alConfirmar={modalConfirmacion.alConfirmar}
        alCancelar={() => setModalConfirmacion((prev) => ({ ...prev, abierto: false }))}
      />

      {/* Modal Selector de APU */}
      {capituloSeleccionadoApu && (
        <ModalSelectorApu
          abierto={Boolean(capituloSeleccionadoApu)}
          capituloNombre={capituloSeleccionadoApu.nombre}
          alCerrar={() => setCapituloSeleccionadoApu(null)}
          alSeleccionar={(apu: Apu, cantidad: number) => {
            agregarItemDesdeApu(presupuestoActivo.id, capituloSeleccionadoApu.id, apu, cantidad)
            setCapituloSeleccionadoApu(null)
          }}
        />
      )}

      {/* Modal Maestro de Insumos */}
      <ModalMaestroInsumos
        abierto={modalInsumosAbierto}
        alCerrar={() => setModalInsumosAbierto(false)}
      />

      {/* Modal Versiones de Presupuesto */}
      <ModalVersionesPresupuesto
        abierto={modalVersionesAbierto}
        presupuesto={presupuestoActivo}
        alCerrar={() => setModalVersionesAbierto(false)}
        alGuardarVersion={(motivo) => guardarVersionPresupuesto(presupuestoActivo.id, motivo)}
        alRestaurarVersion={(vNum) => restaurarVersionPresupuesto(presupuestoActivo.id, vNum)}
      />
    </div>
  )
}

